// src/components/EmailEditModal.tsx
import React, { useState, useEffect } from 'react';
import { 
  getEditableContent as getSampleContent, 
  getFullEmailTemplate as getSampleTemplate,
  DEFAULT_SUBJECT as SAMPLE_SUBJECT
} from '../../templates/sampleRequestEmail';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-hot-toast';
import { fetchWithAuth } from '../../lib/api/fetchWithAuth';
import type { Applicant } from '../../types/applicant';
import { ApplicantStatus } from '../../types/enums.js';

interface EmailEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicant: Applicant | null;
  brandId: string;
  orgId: string;
  onSuccess: () => void;
  setApplicants: React.Dispatch<React.SetStateAction<Applicant[]>>;
  type: 'sample';
}

export function EmailEditModal({ 
  isOpen, 
  onClose, 
  applicant, 
  brandId, 
  orgId, 
  setApplicants
}: EmailEditModalProps) {
  const [emailContent, setEmailContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (applicant) {
      const content = getSampleContent(applicant.firstName);
      setEmailContent(content);
    }
  }, [applicant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailContent || !applicant) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const template = getSampleTemplate(emailContent);

      await fetchWithAuth(`/api/organizations/${orgId}/brands/${brandId}/applicants/${applicant.id}/sample-request`, {
        method: 'POST',
        token,
        body: JSON.stringify({
          emailContent: template
        })
      });
      
      // Optimistically update the applicant's metadata
      setApplicants(prev => prev.map(app => 
        app.id === applicant.id 
          ? {
              ...app,
              sampleRequestSent: true,
              status: ApplicantStatus.SAMPLE_REQUESTED
            }
          : app
      ));

      toast.success('Sample request sent successfully');
      onClose();
    } catch (error) {
      console.error('Failed to send sample request:', error);
      toast.error('Failed to send sample request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !applicant) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-6xl">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Edit Email Template</h2>
        
        {/* Subject Field */}
        <div className="mb-4">
          <label className="text-sm text-gray-400 block mb-2">Subject</label>
          <input
            type="text"
            value={SAMPLE_SUBJECT}
            onChange={(e) => setEmailContent(e.target.value)}
            className="w-full px-4 py-2 bg-gray-700 text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* HTML Editor */}
          <div>
            <div className="text-sm text-gray-400 mb-2">HTML</div>
            <textarea
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              className="w-full h-[500px] p-4 bg-gray-700 text-gray-100 rounded-lg font-mono text-sm focus:outline-none whitespace-pre-wrap"
              spellCheck={false}
            />
          </div>

          {/* Preview */}
          <div>
            <div className="text-sm text-gray-400 mb-2">Preview</div>
            <iframe
              srcDoc={getSampleTemplate(emailContent).body}
              className="w-full h-[500px] bg-white rounded-lg"
              style={{ border: 'none' }}
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-gray-100 rounded hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500"
          >
            Send Email
          </button>
        </div>
      </div>
    </div>
  );
}